import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { getToken } from "firebase/app-check";
import { getAppCheckInstance } from "./firebase";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

// Shared state across all components
let optionsSet = false;
let mapsLoaded = false;
let placesLoaded = false;
let markerLoaded = false;
let appCheckConfigured = false;

/**
 * Initialize Google Maps options (only called once globally)
 */
function initGoogleMapsOptions(): boolean {
  if (!GOOGLE_MAPS_API_KEY) {
    return false;
  }

  if (!optionsSet) {
    setOptions({
      key: GOOGLE_MAPS_API_KEY,
      v: "weekly",
    });
    optionsSet = true;
  }

  return true;
}

/**
 * Configure App Check for Google Maps
 * This connects Firebase App Check to Google Maps API to satisfy enforcement requirements
 */
async function configureAppCheckForMaps(): Promise<void> {
  if (appCheckConfigured) return;

  const appCheck = getAppCheckInstance();
  if (!appCheck) {
    // App Check not initialized yet - this is fine for development
    // or when auth is disabled
    return;
  }

  try {
    const { Settings } = (await importLibrary("core")) as google.maps.CoreLibrary;
    const settings = Settings.getInstance();
    settings.fetchAppCheckToken = () =>
      getToken(appCheck, /* forceRefresh = */ false);
    appCheckConfigured = true;
  } catch (error) {
    console.error("Failed to configure App Check for Maps:", error);
  }
}

/**
 * Load the maps library
 */
export async function loadMapsLibrary(): Promise<void> {
  if (mapsLoaded) return;

  if (!initGoogleMapsOptions()) {
    throw new Error("Google Maps API key niet geconfigureerd");
  }

  await importLibrary("maps");
  await configureAppCheckForMaps();
  mapsLoaded = true;
}

/**
 * Load the places library
 */
export async function loadPlacesLibrary(): Promise<void> {
  if (placesLoaded) return;

  if (!initGoogleMapsOptions()) {
    throw new Error("Google Maps API key niet geconfigureerd");
  }

  await importLibrary("places");
  await configureAppCheckForMaps();
  placesLoaded = true;
}

/**
 * Load the marker library
 */
export async function loadMarkerLibrary(): Promise<void> {
  if (markerLoaded) return;

  if (!initGoogleMapsOptions()) {
    throw new Error("Google Maps API key niet geconfigureerd");
  }

  await importLibrary("marker");
  await configureAppCheckForMaps();
  markerLoaded = true;
}

/**
 * Check if maps library is loaded
 */
export function isMapsLoaded(): boolean {
  return mapsLoaded;
}

/**
 * Check if places library is loaded
 */
export function isPlacesLoaded(): boolean {
  return placesLoaded;
}

/**
 * Check if marker library is loaded
 */
export function isMarkerLoaded(): boolean {
  return markerLoaded;
}

/**
 * Check if API key is configured
 */
export function hasApiKey(): boolean {
  return !!GOOGLE_MAPS_API_KEY;
}
